<script lang="ts">
  /**
   * App root for anything Universal Website Script
   */
  import { getContext, onDestroy } from "svelte"
  import AppWrapper from "website_script/App/AppWrapper.svelte"
  import EngagementWidget from "./EngagementWidget/index.svelte"
  import ContactWidget from "./ContactWidget/index.svelte"
  import FloatingActionButton from "./FloatingActionButton/index.svelte"
  import StandalonePrompts from "./StandalonePrompts/index.svelte"
  import WidgetRouter from "./WidgetRouter/index.svelte"
  import InlineWidgets from "./InlineWidgets/index.svelte"
  import { trackFunction } from "website_script/lib/tracking"
  import {
    customThemeForModuleType,
    getFlowTypeFromModuleType,
    getModuleTypeFromFlowType,
  } from "./lib/Helpers"
  import Modal from "./components/Modal.svelte"
  import ModuleContainer from "./components/ModuleContainer.svelte"
  import IntegrationTypes from "./constants/IntegrationTypes"
  import {
    buildUrlData,
    type BuildUrlDataOutput,
  } from "./lib/buildModuleUrlData"
  import buildModuleUrl from "./lib/buildModuleUrl"
  import abTesting from "./lib/abTesting"
  import ModuleTypes from "./constants/ModuleTypes"
  import { PriceAlertStatus, setPriceAlertStatus } from "./stores/app"
  import type { Texts } from "website_script/types/texts"
  import type { Settings } from "website_script/types/settings"
  import type { Environment } from "website_script/types/environment"
  import type { DealerTheme } from "conversion_flows/shared/contexts/dealerTheme"
  import { get } from "svelte/store"
  import {
    CallbackNames,
    type Config,
    type CustomModalOpts,
  } from "./types/common"
  import AUModal from "component_library/components/svelte/ui/AUModal.svelte"
  import CallFlow from "./components/CallFlow.svelte"
  import { leadVerificationPoll } from "./lib/leadVerificationPoll"
  import { getFlowContextFromModuleData } from "conversion_flows/lib/helpers"
  import {
    FlowEventAction,
    type GenericFlowEvent,
    type FlowEvent,
    StartFlowData,
  } from "conversion_flows/types/flowEvent"
  import { moduleTypeToFlow } from "./lib/events"
  import { FlowType } from "conversion_flows/types/scalars"
  import MessageFlow from "./components/MessageFlow.svelte"
  import {
    setFlowsContext,
    TriggerInputFormFlowFn,
    TriggerSimpleFlowFn,
    type TriggerFlowFn,
  } from "./contexts/flows"
  import {
    getFlowStatus,
    isFlowAvailable,
    isFlowGenerallyAvailable,
  } from "./lib/flowAvailability"
  import { ActionType } from "DealerApp/Settings/webModules/operations/getOptions"

  const settings = getContext<Settings>("settings")
  const texts = getContext<Texts>("texts")
  const dealerTheme = getContext<DealerTheme>("dealerTheme")
  const config = getContext<Config>("config")
  const { abTesting: abTestingStore } = getContext<Environment>("environment")

  let modal: Modal
  let themedModal: AUModal
  let url: string | null
  let moduleData: BuildUrlDataOutput
  let moduleTypeForModal: ModuleTypes
  let flowType: FlowType | undefined
  let leadflowId: number | undefined
  let leadVerificationPollInterval: ReturnType<typeof setInterval> | null = null

  // Set currently running experiments in the store
  abTestingStore.update((store) => ({
    ...store,
    runningExperiments: abTesting.getRunningExperiments(),
  }))

  const handleFlowOpenEvent = ({
    moduleData,
  }: {
    moduleData: BuildUrlDataOutput
  }) => {
    const event: GenericFlowEvent = {
      action: FlowEventAction.leadFlowOpened,
      data: {
        customerDetails: null,
        flowContext: getFlowContextFromModuleData({
          moduleData,
          locale: settings.locale,
        }),
      },
    }
    handleFlowEvent({
      detail: event,
    })
  }

  /** Opens Modal with module based on url data, minimizes the Drawer */
  const onModalTrigger = (urlBuildData: BuildUrlDataOutput) => {
    moduleData = urlBuildData
    url = buildModuleUrl(urlBuildData)
    moduleTypeForModal = moduleData.moduleType

    // Make sure we open all flows in `AUModal` (except for Trade-In and other legacy flows)
    const _flowType = getFlowTypeFromModuleType(moduleTypeForModal)
    if (
      urlBuildData.moduleType !== ModuleTypes.TRADE_IN &&
      _flowType !== null
    ) {
      modal.close()
      flowType = _flowType
      themedModal.open()
    } else {
      modal.show()
    }

    /** Trigger event when module opens */
    handleFlowOpenEvent({ moduleData })
  }

  /**
   * Triggers a flow by opening the `AUModal`.
   */
  const triggerFlow: TriggerFlowFn = (
    flow,
    integrationType,
    urlData,
    elData,
    leadId,
    leadToken
  ) => {
    if (flow.action_type === "simple") {
      triggerSimpleAction(flow)
    } else if (flow.action_type === "input_form") {
      triggerInputFormAction(flow, {
        integrationType,
        urlData,
        elData,
        leadId,
        leadToken,
      })
    }
    trackFunction({
      entity: "lead",
      element: `flow from ${integrationType}`,
      action: "displayed",
      flow: flow.flow_type,
      type: "interacted_with_cta",
    })
  }

  setFlowsContext({ triggerFlow })

  const triggerInputFormAction: TriggerInputFormFlowFn = (flow, opts) => {
    const urlBuildData = buildUrlData({
      settings,
      abTesting: $abTestingStore,
      moduleType: getModuleTypeFromFlowType(flow.flow_type),
      conversionFlow: flow.flow_type,
      leadFlowId: flow.lead_flow_id,
      integrationType: opts.integrationType,
      elData: opts.elData,
      leadId: opts.leadId,
      leadToken: opts.leadToken,
    })
    if (flow.flow_type === "trade_in") {
      // open Trade-In flow in `Modal` instead
      themedModal.close()
      onModalTrigger({ ...urlBuildData, ...opts.urlData })
    } else {
      moduleData = urlBuildData
      moduleTypeForModal = moduleData.moduleType
      flowType = flow.flow_type
      leadflowId = flow.lead_flow_id
      themedModal.open()

      /** Trigger event when module opens */
      handleFlowOpenEvent({ moduleData })
    }
  }

  const triggerSimpleAction: TriggerSimpleFlowFn = (flow) => {
    if (!flow.action_execution)
      throw new Error("Action execution for simple flow not found")

    if (flow.action_execution === "link") {
      const whereToOpen = flow.open_in_new_tab ? "_blank" : "_self"
      window.open(flow.url, whereToOpen)
    } else if (flow.action_execution === "js_snippet") {
      if (flow.js_snippet) {
        try {
          // Creates isolated execution environment using "with + new Function" pattern
          // It restricts the execution scope of the code
          // It will prioritize the properties of the provided object before looking further up the scope chain
          const scope = {}
          const script = new Function(
            "scope",
            `with(scope) { ${flow.js_snippet} }`
          )
          script(scope)
        } catch (error) {
          console.error("Error executing JS snippet:", error)
        }
      } else {
        throw new Error("JS snippet not provided")
      }
    }
  }

  /**
   * Opens a custom modal with the given module type and options.
   *
   * Currently does not support Custom flows and Simple flows
   */
  const openCustomModal = ({
    detail: { moduleType, opts },
  }: CustomModalOpts) => {
    const elData = {
      sellerUrl: opts?.sellerUrl,
      customData: opts?.customData,
      stockId: opts?.stockId ?? null,
      integrationId: opts?.integrationId ?? null,
      carUrl: opts?.sellerUrl ?? null,
      departmentId: opts?.departmentId ?? null,
      hasExternalLogo: false,
    }
    const buildUrlDataInput = {
      settings,
      elData,
      moduleType,
      abTesting: get(abTestingStore),
      integrationType: IntegrationTypes.CUSTOM,
      conversionFlow: opts?.conversionFlow,
      departmentUuid: opts?.departmentId ?? null,
      leadFlowId: opts?.leadFlowId,
    }

    const urlBuildData = buildUrlData(buildUrlDataInput)
    const flowType = getFlowTypeFromModuleType(urlBuildData.moduleType)

    if (!flowType) throw new Error("Flow type not found")

    const flow = {
      flow_type: flowType,
      lead_flow_id: opts?.leadFlowId,
      // Open custom modal function cannot open custom flows currently
      // Only custom flows can have action_type "simple", therefore it is ok to assign "input_form" here
      action_type: "input_form" as ActionType,
    }

    // When there is a specific URL, we can only check if the flow is generally available, as we don't know whether there is a car or not
    // When there is no URL, we can check if the flow is available now for the current URL
    if (
      opts?.sellerUrl
        ? !isFlowGenerallyAvailable(settings, flowType)
        : !isFlowAvailable(settings, flowType)
    ) {
      throw new Error(`Flow ${flowType} is not available`)
    }

    triggerFlow(
      flow,
      IntegrationTypes.CUSTOM,
      urlBuildData,
      undefined,
      undefined,
      undefined
    )
  }

  /** Scrolls contents of the modal to the top */
  const scrollToTop = () => {
    modal.scrollToTop()
  }

  const trackCompletedEvent = (
    data: GenericFlowEvent["data"],
    details?: string
  ) => {
    trackFunction({
      entity: "lead",
      action: "completed",
      details,
      type: "conversion",
      flow: moduleTypeToFlow(data.flowContext.flowType),
    })
  }

  const checkForLeadVerification = (data: GenericFlowEvent["data"]) => {
    if (!data.flowContext.checkLeadVerificationUrl) return

    leadVerificationPollInterval = leadVerificationPoll(
      data.flowContext.checkLeadVerificationUrl,
      () => {
        const { checkLeadVerificationUrl, ...restOfFlowContext } =
          data.flowContext
        const eventData = {
          ...data,
          flowContext: {
            ...restOfFlowContext,
          },
        }
        handleConfigCallback(CallbackNames.onLeadFlowCompleted, eventData)

        trackCompletedEvent(eventData, "from email")
      }
    )
  }

  const handleConfigCallback = (callbackName: CallbackNames, data: unknown) => {
    const callback = config[callbackName]
    if (typeof callback === "function") {
      callback(data)
    }
  }

  // The function is called to send information about status for flows on app load
  // It has been made to help dealers determine whether certain custom buttons should be rendered
  handleConfigCallback(CallbackNames.onFlowStatusKnown, getFlowStatus(settings))

  const actionHandlers = {
    setPriceAlertStatus,
    checkForLeadVerification,
    startFlow: (data: StartFlowData) => {
      triggerFlow(
        data,
        IntegrationTypes.CROSS_FLOW_CTA,
        undefined,
        undefined,
        data.lead_id,
        data.lead_token
      )
    },
    completeFlow: () => {
      themedModal.close()
    },
  }

  /** Handles event from the flow and calls the defined callbacks */
  const handleFlowEvent = ({
    detail: { action, data },
  }: {
    detail: FlowEvent | GenericFlowEvent
  }) => {
    if (!action) return

    switch (action) {
      case "setPriceAlertStatus":
        actionHandlers[action](data as PriceAlertStatus)
        break
      case "checkForLeadVerification":
        actionHandlers[action](data as GenericFlowEvent["data"])
        break
      case "startFlow":
        actionHandlers[action](data as StartFlowData)
        break
      case "completeFlow":
        actionHandlers[action]()
        break
    }

    // Capitalize first letter of callback so that action: `carDetailsSent` is firing `onCarDetailsSent` callback
    // TODO: perhaps refactor the naming so that we don't need to do this
    const callbackName = `on${action.charAt(0).toUpperCase()}${action.slice(
      1
    )}` as CallbackNames
    if (action === "leadFlowCompleted") {
      // Because the submission event is tracked sync right after the flow event is dispatched,
      // we need to make sure this event is fired after that one
      setTimeout(() => {
        trackCompletedEvent(data as GenericFlowEvent["data"])
      }, 0)
    }
    handleConfigCallback(callbackName, data)
  }

  onDestroy(() => {
    if (leadVerificationPollInterval) {
      clearInterval(leadVerificationPollInterval)
    }
  })

  const getModalTitle = (flowId: number): string => {
    const title = settings.leadFlows.flowTitles[flowId]
    if (!title) throw new Error(`Flow with id ${flowId} not found`)
    return title
  }
</script>

<svelte:window on:modalOpen={openCustomModal} />
{#if settings.activateEngagementWidget}
  <EngagementWidget {onModalTrigger} />
{/if}

<Modal
  customTheme={customThemeForModuleType(moduleTypeForModal, dealerTheme)}
  bind:this={modal}
>
  <ModuleContainer
    moduleType={moduleTypeForModal}
    {url}
    {moduleData}
    locale={settings.locale}
    on:viewChanged={scrollToTop}
    on:flowEvent={handleFlowEvent}
  />
</Modal>

<AppWrapper>
  <InlineWidgets on:flowEvent={handleFlowEvent} />

  <ContactWidget />

  <FloatingActionButton />

  <StandalonePrompts />

  <AUModal
    bind:this={themedModal}
    closeLabel={texts.close}
    title={leadflowId ? getModalTitle(leadflowId) : ""}
    id={`au-modal-${flowType}`}
  >
    {#if flowType === "call"}
      <CallFlow on:flowEvent={handleFlowEvent} />
    {:else if flowType === "message"}
      <MessageFlow on:flowEvent={handleFlowEvent} />
    {:else if flowType === "router"}
      <WidgetRouter />
    {:else}
      <ModuleContainer
        moduleType={moduleTypeForModal}
        {moduleData}
        url={null}
        locale={settings.locale}
        on:viewChanged={scrollToTop}
        on:flowEvent={handleFlowEvent}
      />
    {/if}
  </AUModal>
</AppWrapper>
